import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Users } from "lucide-react";
import { toast } from "sonner";
import PremiumPortal from "./PremiumPortal";
import "../portfolio/styles.css";

const API = process.env.REACT_APP_BACKEND_URL;

/* ---------- in-app browser detection ---------- */
function isInAppBrowser() {
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|Instagram|Twitter|LinkedIn|MicroMessenger|Line\/|Snapchat|Pinterest|TikTok/i.test(ua)
    || (/iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua))
    || (/Android/.test(ua) && /wv/.test(ua));
}

/* ---------- auth form ---------- */
function AuthGate({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showInApp] = useState(() => isInAppBrowser());

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const url = mode === "register" ? `${API}/api/auth/register` : `${API}/api/auth/login`;
      const body = mode === "register" ? form : { email: form.email, password: form.password };
      const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      onAuth(d.user);
    } catch (err) { setError(err.message); }
    setBusy(false);
  };

  const googleLogin = async () => {
    try {
      sessionStorage.setItem("post_auth_dest", "/premium");
      const r = await fetch(`${API}/api/auth/google/url`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ redirect_uri: window.location.origin + "/auth/google" }),
      });
      const d = await r.json();
      if (d.url) window.location.href = d.url;
      else toast.error("Could not start Google sign-in");
    } catch { toast.error("Network error"); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 tribal-bg tribal-bg-pattern relative overflow-hidden" data-testid="premium-auth">
      {/* Warli decorative borders */}
      <div className="absolute top-0 left-0 right-0 tribal-dancers" />
      <div className="absolute bottom-0 left-0 right-0 tribal-triangles" />
      <div className="absolute top-6 right-6 tribal-sun w-12 h-12 md:w-16 md:h-16 opacity-60" style={{ backgroundSize: "contain" }} />

      {/* Warli art illustration — faint in background */}
      <img src="/warli-art/peacock.jpg" alt="" className="absolute bottom-0 left-0 w-48 md:w-72 opacity-[0.06] pointer-events-none select-none" style={{ filter: "brightness(1.8)" }} />

      <div className="tribal-card max-w-md w-full p-8 md:p-10 relative z-10" style={{ borderRadius: "12px" }}>
        <div className="text-center">
          <Users className="mx-auto mb-4 text-[#B5793F]" size={26} strokeWidth={1.5} />
          <h1 className="tribal-title text-3xl font-normal mb-2">Welcome</h1>
          <p className="text-sm tribal-text leading-relaxed mb-2">Sign in to access your exclusive content</p>
          <div className="tribal-divider my-6" />
        </div>

        {showInApp && (
          <div className="mb-4 p-3 rounded-lg border border-[#B5793F40] text-xs text-[#C6A97A]" style={{ background: "rgba(181,121,63,0.1)" }}>
            For the best experience, open this page in your default browser (Safari / Chrome).
          </div>
        )}

        <button onClick={googleLogin} data-testid="premium-google-btn"
                className="w-full inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full text-sm font-semibold text-[#F1E7D1] bg-[#22180E] border border-[#B5793F44] transition-all hover:-translate-y-0.5 hover:border-[#B5793F88] mb-4">
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.8 12 .8 7.48.8 3.6 3.48 1.76 7.32l3.66 2.84C6.32 7.32 8.92 5.04 12 5.04z"/><path fill="#4285F4" d="M23.2 12.26c0-.8-.08-1.58-.2-2.32H12v4.52h6.3c-.28 1.44-1.1 2.66-2.32 3.48l3.58 2.78c2.1-1.94 3.64-4.8 3.64-8.46z"/><path fill="#FBBC05" d="M5.42 14.1a7.14 7.14 0 0 1 0-4.2L1.76 7.06a11.15 11.15 0 0 0 0 10.08l3.66-3.04z"/><path fill="#34A853" d="M12 23.2c3 0 5.52-1 7.36-2.7l-3.58-2.78c-1 .68-2.28 1.08-3.78 1.08-3.08 0-5.68-2.08-6.58-4.9l-3.66 2.84C3.6 20.72 7.48 23.2 12 23.2z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-[#B5793F30]" />
          <span className="text-[11px] uppercase tracking-[0.16em] tribal-muted">or with email</span>
          <span className="flex-1 h-px bg-[#B5793F30]" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                   placeholder="Name" data-testid="premium-name-input" className="tribal-input" />
          )}
          <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                 placeholder="Email" type="email" data-testid="premium-email-input" className="tribal-input" />
          <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                 placeholder="Password" type="password" data-testid="premium-password-input" className="tribal-input" />
          {error && <p className="text-sm text-red-400" data-testid="premium-auth-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="premium-auth-submit"
                  className="tribal-btn-primary w-full">
            {busy ? "Please wait..." : mode === "register" ? "Create account" : "Log in"}
          </button>
        </form>

        <p className="text-center text-sm tribal-text mt-5">
          {mode === "login" ? (
            <>New here? <button onClick={() => setMode("register")} className="font-semibold text-[#B5793F] underline underline-offset-2">Create an account</button></>
          ) : (
            <>Already have an account? <button onClick={() => setMode("login")} className="font-semibold text-[#B5793F] underline underline-offset-2">Log in</button></>
          )}
        </p>
      </div>
    </div>
  );
}

/* ---------- standalone premium page ---------- */
export default function PremiumPage() {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => { setUser(u); setStatus("ready"); })
      .catch(() => setStatus("auth"));
  }, []);

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" });
    sessionStorage.removeItem("tribe_user");
    setUser(null);
    setStatus("auth");
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen tribal-bg tribal-bg-pattern">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="h-8 w-48 bg-[#B5793F18] rounded-xl animate-pulse mb-6" />
          <div className="h-4 w-80 bg-[#B5793F10] rounded-full animate-pulse mb-10" />
          <div className="h-64 tribal-surface rounded-lg animate-pulse mt-6" />
        </div>
      </div>
    );
  }

  if (status === "auth") {
    return <AuthGate onAuth={(u) => { setUser(u); setStatus("ready"); }} />;
  }

  return (
    <div className="min-h-screen tribal-bg tribal-bg-pattern relative" data-testid="premium-page">
      {/* Warli decorative borders */}
      <div className="fixed top-0 left-0 right-0 tribal-dancers z-0 opacity-40" />
      <div className="fixed bottom-0 left-0 right-0 tribal-triangles z-0 opacity-30" />

      <header className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pt-8 pb-2 flex items-center justify-end">
        <div className="flex items-center gap-3">
          {user.picture && <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full border border-[#B5793F44]" />}
          <span className="text-sm tribal-text hidden sm:block">{user.name}</span>
          <button onClick={logout} data-testid="premium-logout-btn" title="Log out"
                  className="w-9 h-9 rounded-full border border-[#B5793F30] flex items-center justify-center text-[#8A7E6B] hover:text-[#F1E7D1] hover:border-[#B5793F] transition-colors">
            <LogOut size={14} />
          </button>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 py-10 md:py-14">
        <PremiumPortal user={user} />
      </main>
    </div>
  );
}
